 
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchProductBySlug, fetchCatalog, formatPrice } from '../lib/supabaseClient'

// ===== PRODUCT IMAGE GALLERY — unique images per product =====
// IMPORTANT: Each array must contain UNIQUE image URLs — no duplicates!
// File names must EXACTLY match files in /var/www/sweden-sound/dist/products/
const SLUG_TO_IMAGES_SUPABASE = {
  'ss-h8pro':  [
    '/products/SS-H8PRO.png',
    '/products/SS-H8PRO_1.png',
  ],
  'ss-da15s':  [
    '/products/SS-DA15S.png',
    '/products/SS-DA15S1.png',
  ],
  'ss-da18s':  [
    '/products/SS-DA18S.png',
    '/products/SS-DA18S1.png',
  ],
  'ss-j4012a': [
    '/products/SJ4012.png',
    '/products/SJ4012_1.png',
    '/products/SJ4012_2.png',
  ],
  'ss-j4015a': [
    '/products/SJ4015.png',
    '/products/SJ4015_1.png',
    '/products/SJ4015_2.png',
  ],
  'ss-r10a':   [
    '/products/SS-10RA.png',    // front view
    '/products/SS-10RA_2.png',  // front straight view
    '/products/SS-10RA_1.png',  // back panel
  ],
  'ss-r12a':   [
    '/products/SS-12RA.png',
    '/products/SS-12RA_1.png',
    '/products/SS-12RA_2.png',
  ],
  'ss-r15a':   [
    '/products/SS-15RA.png',
    '/products/SS-15RA1.png',
    '/products/SS-15RA2.png',
    '/products/SS-15RA3.png',
    '/products/SS-15RA4.png',
  ],
  'ss-280a':   ['/products/ss-280a.png'],
  'ss200m-uv': ['/products/ss200m-uv.png'],
  'ss-pa-21':   [

 
    '/products/SS-PA_2.1.png',
    '/products/SS-PA_2.1_1.png',
    '/products/SS-PA_2.1_2.png',
    '/products/SS-PA_2.1_3.png',
    '/products/SS-PA_2.1_4.png',
    '/products/SS-PA_2.1_5.png',
  ],
    'ss-dd1238a': [
      '/products/SS-DD1238A.png',
      '/products/SS-DD1238A_1.png',
    ],
}

// ===== Deduplicate image array =====
function dedupImages(images) {
  if (!images || images.length === 0) return []
  const seen = new Set()
  return images.filter(url => {
    if (seen.has(url)) return false
    seen.add(url)
    return true
  })
}

// ===== Get product images (guaranteed unique) =====
function getProductPageImages(product) {
  // 1. Local mapped gallery (MOST reliable — avoids broken Supabase URLs)
  const gallery = SLUG_TO_IMAGES_SUPABASE[product?.slug]
  if (gallery && gallery.length > 0) {
    return dedupImages(gallery)
  }
  // 2. API-provided images — only if local paths (no supabase/external)
  if (product?.images && product.images.length > 0) {
    const localOnly = product.images.filter(img =>
      typeof img === 'string' && !img.includes('supabase.co') && !img.includes('http')
    )
    if (localOnly.length > 0) {
      return dedupImages(localOnly)
    }
  }
  // 3. Fallback: single image from DB
  if (product?.cover_image) return [product.cover_image]
  if (product?.image) return [product.image]
  // 4. Placeholder
  return [`/products/${product?.slug}.png`]
}

// ===== Related products images =====
const RELATED_IMG_MAP = {
  'ss-da15s':  '/products/SS-DA15S.png',
  'ss-da18s':  '/products/SS-DA18S.png',
  'ss-h8pro':  '/products/SS-H8PRO.png',
  'ss-r10a':   '/products/SS-10RA.png',
  'ss-r12a':   '/products/SS-12RA.png',
  'ss-r15a':   '/products/SS-15RA.png',
  'ss-j4012a': '/products/SJ4012.png',
  'ss-j4015a': '/products/SJ4015.png',
  'ss-280a':   '/products/ss-280a.png',
  'ss200m-uv': '/products/ss200m-uv.png',
  'ss-pa-21':  '/products/SS-PA_2.1.png',
  'ss-dd1238a':'/products/SS-DD1238A.png',
}

// ===== Format description: convert bullet list to flowing text =====
function formatDescription(slug, raw) {
  if (!raw) return raw
  if (slug !== 'ss-pa-21') return raw
  if (!raw.includes('◎') && !raw.includes('•') && !raw.includes('- ')) return raw
  const p1 = 'Активная акустическая система SS-PA 2.1 формата 2.1 PA общей мощностью 1000 Вт. Комплект включает 12-дюймовый сабвуфер мощностью 300 Вт RMS на базе 3-канального усилительного модуля и две пассивные 8-дюймовые акустические системы по 200 Вт RMS каждая. Корпус сабвуфера выполнен из окрашенного МДФ, сателлитов — из ABS-пластика с деревянным покрытием.'
  const p2 = 'Система поддерживает беспроводное соединение Bluetooth, функцию TWS для объединения двух комплектов в стереопару, а также встроенный MP3-плеер с цветным LCD-дисплеем. В комплект поставки входят 2 кабеля длиной 7 метров и 2 стойки-треноги высотой 1.5 метра.'
  return p1 + '\n\n' + p2
}

function getRelatedImage(product) {
  return RELATED_IMG_MAP[product?.slug] || `/products/${product?.slug}.png`
}

function ArrowLeft({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function ArrowRight({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
}

export default function ProductPage() {
  const { slug } = useParams()
  const [product, setProduct] = useState(null)
  const [related, setRelated] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImage, setCurrentImage] = useState(0)
  const [isImageHovered, setIsImageHovered] = useState(false)
  const touchStartX = useRef(0)

  const isDealer = typeof window !== 'undefined' && localStorage.getItem('dealer_mode') === 'true'

  useEffect(() => {
    let cancelled = false

 
    setLoading(true)
    setCurrentImage(0)

    fetchProductBySlug(slug)
      .then((p) => { if (!cancelled) { setProduct(p); setLoading(false) } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false) } })

    fetchCatalog()
      .then(({ products }) => { if (!cancelled) setRelated(products.filter((p) => p.slug !== slug).slice(0, 4)) })
      .catch(() => {})

    return () => { cancelled = true }
  }, [slug])

  const images = product ? getProductPageImages(product) : []
  const maxIndex = images.length - 1

  const goNext = useCallback(() => setCurrentImage((prev) => (prev >= maxIndex ? 0 : prev + 1)), [maxIndex])
  const goPrev = useCallback(() => setCurrentImage((prev) => (prev <= 0 ? maxIndex : prev - 1)), [maxIndex])

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX }
  const onTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].screenX
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev() }
  }

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'ArrowRight') goNext(); if (e.key === 'ArrowLeft') goPrev() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [goNext, goPrev])

  if (loading) {
    return (
      <div className="min-h-screen bg-ss-white flex items-center justify-center pt-16">
        <div className="w-10 h-10 border-3 border-ss-border border-t-ss-dark rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ss-white flex items-center justify-center px-4 pt-16">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/catalog" className="text-ss-dark hover:underline">← Вернуться в каталог</Link>
        </div>
      </div>
    )
  }

 

  if (!product) return null

  // dealer_price now comes from API (added by server)
  const dealerPrice = product.dealer_price
  const showDealerPrice = isDealer && dealerPrice != null

  // specs from API is an array: [{key, value}, ...]
  const specs = product.specs || []

  return (
    <div className="min-h-screen bg-ss-white pt-16">
      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <nav className="flex items-center gap-2 text-sm text-ss-muted">
          <Link to="/" className="hover:text-ss-dark transition-colors">Главная</Link>
          <span>/</span>
          <Link to="/catalog" className="hover:text-ss-dark transition-colors">Каталог</Link>
          <span>/</span>
          <span className="text-ss-text font-medium">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div
              className="relative aspect-square bg-white border border-gray-100 rounded-2xl overflow-hidden select-none"
              onMouseEnter={() => setIsImageHovered(true)}
              onMouseLeave={() => setIsImageHovered(false)}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              <div
                className="w-full h-full flex items-center justify-center p-8 lg:p-12"
                style={{
                  transition: 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)',
                  transform: isImageHovered ? 'translateY(-12px)' : 'translateY(0)',
                }}
              >
                <img
                  src={images[currentImage]}
                  alt={`${product.name} — фото ${currentImage + 1}`}
                  className="max-w-full max-h-full object-contain"
                  style={{
                    filter: isImageHovered
                      ? 'drop-shadow(0 40px 60px rgba(0,0,0,0.2))'
                      : 'drop-shadow(0 25px 40px rgba(0,0,0,0.15))',
                    transition: 'filter 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)',
 
                  }}
                  draggable={false}
                  key={currentImage}
                />
              </div>

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
                             bg-white/90 backdrop-blur-sm rounded-full shadow-md text-ss-text
                             hover:bg-white hover:text-ss-dark transition-all active:scale-95"
                    aria-label="Предыдущее фото"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={goNext}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center
                             bg-white/90 backdrop-blur-sm rounded-full shadow-md text-ss-text
                             hover:bg-white hover:text-ss-dark transition-all active:scale-95"
                    aria-label="Следующее фото"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  {/* Counter */}
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-ss-text">
                    {currentImage + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl border-2 overflow-hidden bg-white transition-all
                      ${i === currentImage
                        ? 'border-ss-dark shadow-md'
                        : 'border-gray-200 hover:border-gray-400'
                      }`}
                  >
                    <img
                      src={img}

 
                      alt={`${product.name} — миниатюра ${i + 1}`}
                      className="w-full h-full object-contain p-2"
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ss-muted mb-2">
                {product.category_name || product.category || "Home Series"}
              </p>
              <h1 className="text-3xl lg:text-4xl font-bold text-ss-text tracking-tight mb-4">
                {product.name}
              </h1>
              <p className="text-ss-muted leading-relaxed">
                {product.short_description}
              </p>
            </div>

            {/* ===== PRICE BLOCK ===== */}
            {showDealerPrice ? (
              <div className="flex items-baseline gap-3 flex-wrap">
                {/* Retail — smaller, grey, strikethrough */}
                <span className="text-xl font-medium text-gray-400 line-through decoration-gray-300">
                  {formatPrice(product.price)}
                </span>
                {/* Dealer — large, bright red */}
                <span className="text-3xl font-bold text-red-600 tracking-tight">
                  {formatPrice(dealerPrice)}
                </span>
                {/* "YOUR PRICE" badge */}
                <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white uppercase tracking-wider">
                  ВАША ЦЕНА
                </span>
              </div>
            ) : (
              /* Guest — one standard black price */
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-ss-text tracking-tight">
                  {formatPrice(product.price)}
                </span>
              </div>
            )}

            {/* ===== SPECS — API returns array [{key, value}] ===== */}

 
            {specs.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-ss-muted mb-4">
                  Характеристики
                </h3>
                <dl className="space-y-3">
                  {specs.map((spec, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-gray-100">
                      <dt className="text-sm text-ss-muted">{spec.key}</dt>
                      <dd className="text-sm font-medium text-ss-text">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.08em] text-ss-muted mb-3">
                  Описание
                </h3>
                <div className="text-sm text-ss-muted leading-relaxed space-y-4">
                  {formatDescription(product.slug, product.description).split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="pt-4">
              {React.createElement('a', {href: '/contacts', style: {backgroundColor: '#1a1a1a', color: '#ffffff', padding: '16px 32px', borderRadius: '12px', textDecoration: 'none', display: 'inline-block', fontWeight: 500, fontSize: '16px', fontFamily: 'inherit'}, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = '#333333' }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = '#1a1a1a' }}, 'Связаться с нами')}
            </div>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-20 pt-16 border-t border-gray-100">
            <h2 className="text-xl font-semibold text-ss-text mb-8">Похожие товары</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}

 
                  to={`/product/${p.slug}`}
                  className="group block"
                >
                  <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm
                                hover:shadow-lg transition-shadow duration-300">
                    <div className="aspect-square overflow-hidden bg-gray-50 p-4">
                      <img
                        src={getRelatedImage(p)}
                        alt={p.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => { e.target.src = `/products/${p.slug}.png` }}
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                        {p.category_name || p.category}
                      </p>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 truncate">{p.name}</h3>
                      {/* Related product price */}
                      {isDealer && p.dealer_price ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs text-gray-400 line-through">
                            {formatPrice(p.price)}
                          </span>
                          <span className="text-base font-bold text-red-600">
                            {formatPrice(p.dealer_price)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-base font-bold text-gray-900">{formatPrice(p.price)}</p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

